// Globals
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getLinkingTools } from '../../feature';
import {
  createInterLink,
  deleteInterLink,
  exportInterLinks,
  importInterLink,
  updateInterLink,
} from '../../feature/actionCreators';

// Components
import { Button, Table } from '@/ui';
import { PlusIcon, TrashIcon } from '@/icons';
import { Modal, ModalSize, showSuccessToast } from '@/components';
import { StyledButtons, StyledContainer, StyledPagination } from './LinkingToolContainerStyles';
import { ImportLinksForm, LinkingToolForm } from '../../components';

// Models
import { IInterLink } from '@/models/linkingTool.model';

// Modules
import { Header } from '@/modules/Layout';
import { selectChannel } from '@/modules/Channels';

// Helpers
import { linkingToolColumns, ModalViews, TCreateLinkingTemplateFormValues } from '../../helpers';
import { useGetInterLinks } from '../../hooks';
import { CreateInterLinkDTO, GetInterLinksDTO, InterLinksImportDTO, UpdateInterLinkDTO } from '../../dtos';
import { TInterLinksImportFormValues, TUpdateLinkingTemplateFormValues } from '../../helpers/types';
import { fileDownload } from '@/utils';

const LinkingToolContainer = () => {
  const { t } = useTranslation();
  const channel = useAppState(selectChannel);
  const [modalView, setModalView] = useState<ModalViews | null>(null);
  const [selectedInterLink, setSelectedInterLink] = useState<IInterLink | null>(null);

  const { isLoading, interLinks, pagination } = useGetInterLinks({
    channelId: channel?.id,
  });
  const dispatch = useAppDispatch();

  const handleCloseModal = () => {
    setSelectedInterLink(null);
    setModalView(null);
  };

  const handleOpenEdit = (record: IInterLink) => {
    setSelectedInterLink(record);
    setModalView(ModalViews.EDIT);
  };

  const handleOpenCreate = () => {
    setSelectedInterLink(null);
    setModalView(ModalViews.CREATE);
  };

  const handleDelete = (record: IInterLink) => {
    setSelectedInterLink(record);
    setModalView(ModalViews.DELETE);
  };

  const handleOpenImport = () => {
    setSelectedInterLink(null);
    setModalView(ModalViews.IMPORT);
  };

  const handleSubmitCreateInterLink = useCallback(
    async (values: TCreateLinkingTemplateFormValues) => {
      setSelectedInterLink(null);
      if (!channel?.id) return;
      const createDto = new CreateInterLinkDTO({ ...values, channelId: channel.id });
      const getDto = new GetInterLinksDTO({ channelId: channel.id });
      try {
        await dispatch(createInterLink(createDto)).unwrap();
        await dispatch(getLinkingTools(getDto)).unwrap();
        setModalView(null);
      } catch {}
    },
    [channel?.id, dispatch],
  );

  const handleSubmitEditInterLink = useCallback(
    async (values: TUpdateLinkingTemplateFormValues) => {
      if (!selectedInterLink?.id || !channel?.id) return;
      const updateDto = new UpdateInterLinkDTO({ ...values, id: selectedInterLink.id });
      try {
        await dispatch(updateInterLink(updateDto)).unwrap();
        setSelectedInterLink(null);
        setModalView(null);
      } catch {}
    },
    [channel?.id, dispatch, selectedInterLink?.id],
  );

  const handleSubmitInterLinkImport = useCallback(
    async (values: TInterLinksImportFormValues) => {
      setSelectedInterLink(null);
      if (!channel?.id) return;
      const importDto = new InterLinksImportDTO({ ...values, channelId: channel.id });
      try {
        const result = await dispatch(importInterLink(importDto)).unwrap();
        if (result.success) {
          showSuccessToast({ message: t('import_success_message') });
        }
        setModalView(null);
        const getDto = new GetInterLinksDTO({ page: 1, channelId: channel.id });
        await dispatch(getLinkingTools(getDto)).unwrap();
      } catch {}
    },
    [channel?.id, dispatch, t],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedInterLink) return;
    await dispatch(deleteInterLink({ id: selectedInterLink.id })).unwrap();
    setSelectedInterLink(null);
    setModalView(null);
  }, [dispatch, selectedInterLink]);

  const handlePageChange = (page: number) => {
    const dto = new GetInterLinksDTO({ page, channelId: channel?.id ?? 0 });
    dispatch(getLinkingTools(dto));
  };

  const handleSearch = debounce(async (event: ChangeEvent<HTMLInputElement>) => {
    const dto = new GetInterLinksDTO({ page: '1', query: event.target.value, channelId: channel?.id ?? 0 });
    dispatch(getLinkingTools(dto));
  }, 500);

  const getModalContent = useCallback(
    (view: ModalViews) => {
      switch (view) {
        case ModalViews.EDIT:
          return (
            <LinkingToolForm
              onSubmit={handleSubmitEditInterLink}
              isLoading={isLoading}
              siteURL={channel?.siteUrl}
              initialValues={selectedInterLink as IInterLink}
            />
          );
        case ModalViews.DELETE:
          return (
            <div>
              <p>{t(`delete_description`)}</p>
              <StyledButtons>
                <Button
                  type="primary"
                  danger
                  size="small"
                  onClick={handleConfirmDelete}
                  icon={<TrashIcon />}
                  loading={isLoading}
                >
                  {t('delete_link')}
                </Button>
              </StyledButtons>
            </div>
          );
        case ModalViews.CREATE:
          return (
            <LinkingToolForm isLoading={isLoading} onSubmit={handleSubmitCreateInterLink} siteURL={channel?.siteUrl} />
          );
        case ModalViews.IMPORT:
          return <ImportLinksForm onSubmit={handleSubmitInterLinkImport} isLoading={isLoading} />;
        default:
          return null;
      }
    },
    [
      channel?.siteUrl,
      handleSubmitCreateInterLink,
      handleSubmitInterLinkImport,
      handleConfirmDelete,
      handleSubmitEditInterLink,
      isLoading,
      selectedInterLink,
      t,
    ],
  );

  const handleExportInterLinks = useCallback(async () => {
    try {
      const file = await dispatch(exportInterLinks({ channelId: channel?.id })).unwrap();
      fileDownload(file, 'text/csv', '_self');
    } catch {}
  }, [dispatch, channel?.id]);

  const title = useMemo(() => {
    switch (modalView) {
      case ModalViews.EDIT:
        return t('title_edit_link', { name: selectedInterLink?.pagePath ?? '' });
      case ModalViews.DELETE:
        return t('delete_link_confirm');
      case ModalViews.CREATE:
        return t('title_create_inner_links');
      case ModalViews.IMPORT:
        return t('import_links');
      default:
        return null;
    }
  }, [modalView, selectedInterLink?.pagePath, t]);

  return (
    <>
      <Header title={t('menu_linking_tool')} suptitle={t('menu_seo')}>
        <StyledButtons>
          <Button type="default" onClick={handleExportInterLinks}>
            {t('btn_redirects_export')}
          </Button>
          <Button type="default" onClick={handleOpenImport}>
            {t('btn_redirects_import')}
          </Button>
          <Button icon={<PlusIcon />} type="primary" onClick={handleOpenCreate}>
            {t('btn_add_inter_link')}
          </Button>
        </StyledButtons>
      </Header>
      <StyledContainer>
        <Table
          columns={linkingToolColumns(t, handleSearch, handleDelete, handleOpenEdit)}
          rowKey="id"
          dataSource={interLinks ?? []}
          loading={isLoading}
          pagination={false}
          size="small"
          virtual
          rowHoverable
        />
        <StyledPagination
          disabled={isLoading}
          total={pagination?.total ?? 0}
          current={pagination?.page ?? 1}
          pageSize={pagination?.pageSize ?? 10}
          onChange={handlePageChange}
          hideOnSinglePage
        />
      </StyledContainer>
      <Modal
        title={title}
        size={modalView === ModalViews.DELETE ? ModalSize.MEDIUM : ModalSize.LARGE}
        visible={!!modalView}
        onCancel={handleCloseModal}
        destroyOnClose
      >
        {!!modalView && getModalContent(modalView)}
      </Modal>
    </>
  );
};

export default LinkingToolContainer;
